import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { useVoiceContext } from "../VoiceContext";
import { SoundItem, useSound } from "./SoundContext";
import { Voice } from "../../types/voice";

// 現在再生中のオーディオタイプ
export type AudioType = "voice" | "music" | null;

// 統合オーディオコンテキストのプロパティ
interface UnifiedAudioContextProps {
  // 現在の再生状態
  currentAudioType: AudioType;
  isAnyPlaying: boolean;

  // 現在再生中のアイテム情報
  currentVoice: Voice | null;
  currentSound: SoundItem | null;

  // 音声関連メソッド
  playVoice: (uuid: string) => void;
  stopVoice: () => void;

  // 音楽関連メソッド
  playSound: (url: string) => void;
  stopSound: () => void;

  // 共通メソッド
  stopAllAudio: () => void;

  // 前後の項目に移動
  previousAudio: () => void;
  nextAudio: () => void;

  // ミュートの切り替え
  toggleMute: () => void;
}

// コンテキスト作成
const UnifiedAudioContext = createContext<UnifiedAudioContextProps | undefined>(undefined);

// コンテキストプロバイダーのプロップス
interface UnifiedAudioProviderProps {
  children: ReactNode;
}

// プロバイダーコンポーネント
export const UnifiedAudioProvider: React.FC<UnifiedAudioProviderProps> = ({ children }) => {
  const voiceContext = useVoiceContext();
  const soundContext = useSound();

  // 現在の再生状態
  const [currentAudioType, setCurrentAudioType] = useState<AudioType>(null);
  const [isAnyPlaying, setIsAnyPlaying] = useState(false);

  // Voice再生状態の監視
  useEffect(() => {
    if (voiceContext.isPlaying) {
      setCurrentAudioType("voice");
      setIsAnyPlaying(true);
    } else if (currentAudioType === "voice") {
      setIsAnyPlaying(false);
      // 他のオーディオが再生中でなければnullに
      if (!soundContext.isPlaying) {
        setCurrentAudioType(null);
      }
    }
  }, [voiceContext.isPlaying, currentAudioType]);

  // Sound再生状態の監視
  useEffect(() => {
    if (soundContext.isPlaying) {
      setCurrentAudioType("music");
      setIsAnyPlaying(true);
    } else if (currentAudioType === "music") {
      setIsAnyPlaying(false);
      // 他のオーディオが再生中でなければnullに
      if (!voiceContext.isPlaying) {
        setCurrentAudioType(null);
      }
    }
  }, [soundContext.isPlaying, currentAudioType]);

  // 音声再生関数 - 元のコンテキストのtoggleAudioを呼び出し
  const playVoice = async (uuid: string) => {
    try {
      // 音楽が再生中なら停止
      if (soundContext.isPlaying && soundContext.playingUrl) {
        await soundContext.toggleAudio(soundContext.playingUrl);
      }
      // 音声を再生 - 元のtoggleAudioを使用
      await voiceContext.toggleAudio(uuid);
    } catch (error) {
      console.error("Error in playVoice:", error);
    }
  };

  // 音声停止関数
  const stopVoice = async () => {
    try {
      if (voiceContext.isPlaying && voiceContext.playingUuid) {
        await voiceContext.toggleAudio(voiceContext.playingUuid);
      }
    } catch (error) {
      console.error("Error in stopVoice:", error);
    }
  };

  // 音楽再生関数 - 元のコンテキストのtoggleAudioを呼び出し
  const playSound = async (url: string) => {
    try {
      // 音声が再生中なら停止
      if (voiceContext.isPlaying && voiceContext.playingUuid) {
        await voiceContext.toggleAudio(voiceContext.playingUuid);
      }
      // 音楽を再生 - 元のtoggleAudioを使用
      await soundContext.toggleAudio(url);
    } catch (error) {
      console.error("Error in playSound:", error);
    }
  };

  // 音楽停止関数
  const stopSound = async () => {
    try {
      if (soundContext.isPlaying && soundContext.playingUrl) {
        await soundContext.toggleAudio(soundContext.playingUrl);
      }
    } catch (error) {
      console.error("Error in stopSound:", error);
    }
  };

  // すべてのオーディオを停止
  const stopAllAudio = () => {
    stopVoice();
    stopSound();
  };

  // 前のアイテムへ移動
  const previousAudio = async () => {
    try {
      if (currentAudioType === "voice" && voiceContext.playingUuid) {
        // Voiceの場合はvoiceContextのpreviousVoiceを呼ぶ
        voiceContext.previousVoice();
      } else if (currentAudioType === "music" && soundContext.playingUrl) {
        // Soundの場合はsoundContextのpreviousSoundを呼ぶ
        soundContext.previousSound();
      }
    } catch (error) {
      console.error("Error in previousAudio:", error);
    }
  };

  // 次のアイテムへ移動
  const nextAudio = async () => {
    try {
      if (currentAudioType === "voice" && voiceContext.playingUuid) {
        // Voiceの場合はvoiceContextのnextVoiceを呼ぶ
        voiceContext.nextVoice();
      } else if (currentAudioType === "music" && soundContext.playingUrl) {
        // Soundの場合はsoundContextのnextSoundを呼ぶ
        soundContext.nextSound();
      }
    } catch (error) {
      console.error("Error in nextAudio:", error);
    }
  };

  // ミュートの切り替え
  const toggleMute = () => {
    if (currentAudioType === "voice" && voiceContext.wavesurferInstance) {
      voiceContext.toggleMute();
    } else if (currentAudioType === "music" && soundContext.wavesurferInstance) {
      soundContext.toggleMute();
    }
  };

  // コンテキスト値
  const contextValue: UnifiedAudioContextProps = {
    currentAudioType,
    isAnyPlaying,
    currentVoice: voiceContext.currentVoice,
    currentSound: soundContext.currentSound,
    playVoice,
    stopVoice,
    playSound,
    stopSound,
    stopAllAudio,
    previousAudio,
    nextAudio,
    toggleMute,
  };

  return <UnifiedAudioContext.Provider value={contextValue}>{children}</UnifiedAudioContext.Provider>;
};

// カスタムフック
export const useUnifiedAudio = (): UnifiedAudioContextProps => {
  const context = useContext(UnifiedAudioContext);
  if (context === undefined) {
    throw new Error("useUnifiedAudio must be used within a UnifiedAudioProvider");
  }
  return context;
};
